import React, { useState, useEffect } from "react";
import EventMessages from "../components/EventMessages";
import HelpGuide from "../components/HelpGuide";
import RefereeAsk from "../components/RefereeAsk";
import Welcome from "../components/Welcome";
import { RefereeMark } from "../SportIcons";
import { attendanceStatus } from "../utils/invite";
import OrganizerTab from "../components/OrganizerTab";
import Wordmark from "../components/Wordmark";
import { PickleballBall } from "../SportIcons";
import {
  collection, query, getDocs, doc, getDoc, setDoc, updateDoc, onSnapshot, orderBy
} from "firebase/firestore";
import { db, registerFcmToken, duprGetPlayerStats, DuprPlayerStats } from "../firebase";
import DuprConnectButton, { DuprConnectState } from "../components/DuprConnectButton";
import { AuthSession, Player, ClubEvent, Participant, Game, Sport, Season, getRatingInfo, calcDUPR, duprLevel, pprLevel, utrLevel, rankPlayers, sharedPositions, gameResult } from "../types";
import { mobilePayLink } from "../utils/mobilepay";
import { useClubSeasons } from "../useClubSeasons";
import { useLeagueStandings } from "../useLeagueStandings";
import { LeagueTable, LeagueRulesCard } from "../components/LeagueTable";
import { AnnouncementsList } from "../components/Announcements";
import NewsFeed from "../components/NewsFeed";
import { isNative, webNotificationPermission, notificationsGranted } from "../nativePush";
import { APP_VERSION } from "../version";
import NightStandings, { nightHasResults } from "../components/NightStandings";
import VersionTag from "../components/VersionTag";
import Avatar from "../components/Avatar";
import CalendarButtons from "../components/CalendarButtons";
import { useClubVenues } from "../components/VenuePicker";
import { sideNames } from "../utils/sides";

const C = {
  card: "var(--sl-card)",
  navy:  "var(--sl-primary)", gold: "var(--sl-accent)", text: "var(--sl-text)",
  muted: "var(--sl-muted)", bg:   "var(--sl-bg)", white: "#ffffff",
  border:"var(--sl-border)", accent: "var(--sl-success)", error: "var(--sl-error)", soft: "var(--sl-soft)",
};
const courtPattern = `<svg width="400" height="400" viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg"><g stroke="var(--sl-primary)" stroke-opacity="0.035" stroke-width="1.5" fill="none"><rect x="20" y="20" width="360" height="360" rx="24"/><rect x="60" y="60" width="280" height="280" rx="16"/><line x1="200" y1="60" x2="200" y2="340"/><line x1="60" y1="140" x2="340" y2="140"/><line x1="60" y1="260" x2="340" y2="260"/><line x1="20" y1="200" x2="200" y2="20"/><line x1="200" y1="380" x2="380" y2="200"/></g></svg>`;
const F = { heading: "'Rajdhani', sans-serif", body: "'Sora', sans-serif" };

/**
 * Skaalaa kuvan neliöksi ja pakkaa JPEGiksi.
 *
 * Puhelimen kuva on tyypillisesti 3-12 Mt, ja avatar on 72 pikseliä.
 * Raakakuvan lähettäminen olisi pelkkää odotusta mobiiliverkossa ja
 * turhaa Storage-laskua. 256 pikseliä riittää myös retina-näytölle.
 *
 * Rajaus tehdään keskeltä, joten pystykuvasta ei tule litistynyttä.
 * imageOrientation lukee EXIF-kierron; ilman sitä puhelimella otettu
 * kuva näkyisi kyljellään.
 */
async function shrinkToSquare(file: File, size = 256): Promise<Blob> {
  // TypeScriptin DOM-tyypit tuntevat vain vanhat arvot "none" ja "flipY".
  // "from-image" on nykyinen spesifikaatio ja selaimet tukevat sitä; ne
  // jotka eivät, jättävät tuntemattoman arvon huomiotta.
  const opts = { imageOrientation: "from-image" } as unknown as ImageBitmapOptions;
  const bitmap = await createImageBitmap(file, opts);
  const side = Math.min(bitmap.width, bitmap.height);
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas");
  ctx.drawImage(
    bitmap,
    (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side,
    0, 0, size, size
  );
  bitmap.close();
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      b => (b ? resolve(b) : reject(new Error("toBlob"))),
      "image/jpeg",
      0.85
    );
  });
}

const card: React.CSSProperties = {
  background: C.card, border: `1px solid ${C.border}`,
  borderRadius: 16, padding: 18, marginBottom: 14,
  boxShadow: "0 4px 16px rgba(22,59,104,0.06)",
};
const btn: React.CSSProperties = {
  padding: "10px 18px", borderRadius: 10, border: "none",
  cursor: "pointer", fontWeight: 700, fontSize: 13, fontFamily: F.body,
};
const input: React.CSSProperties = {
  width: "100%", padding: "10px 12px", boxSizing: "border-box",
  background: C.card, border: `1.5px solid ${C.border}`,
  borderRadius: 10, fontSize: 14, color: C.text,
  fontFamily: F.body, outline: "none", marginBottom: 10,
};

type PlayerTab = "event" | "profile" | "history" | "league" | "news" | "organize";

interface PlayerViewProps {
  session: AuthSession;
  onLogout: () => void;
}

export default function PlayerView({ session, onLogout }: PlayerViewProps) {
  const [tab, setTab]           = useState<PlayerTab>("event");
  const [showHelp, setShowHelp] = useState(false);
  const closeHelp = React.useCallback(() => setShowHelp(false), []);
  const [welcomeDone, setWelcomeDone] = useState(false);
  const [showReferee, setShowReferee] = useState(false);
  const closeReferee = React.useCallback(() => setShowReferee(false), []);
  // Liiga: välilehti näkyy vain, jos klubilla on avoin kausi. Tavallisen
  // pelivuoron pelaaja ei huomaa mitään muutosta.
  const { seasons } = useClubSeasons(session.clubId);
  const activeSeason = seasons.find(s => s.status === "open") ?? null;
  const seasonOf = (id?: string) => seasons.find(s => s.id === id) ?? null;
  // Sessioon lukittu eventId on enää lähtökohta, ei rajoite. Ennen
  // Tapahtuma-välilehti näkyi vain jos pelaaja oli kirjautunut tapahtuman
  // PIN-koodilla; kutsulinkillä tullut ei nähnyt yhtään tapahtumaa eikä
  // päässyt vahvistamaan osallistumistaan mistään.
  const [selectedEventId, setSelectedEventId] = useState<string | null>(session.eventId ?? null);
  // Omat osallistumiset tulevissa tapahtumissa, avaimena tapahtuman id.
  const [myUpcoming, setMyUpcoming] = useState<Record<string, Participant | null>>({});
  const [attendBusy, setAttendBusy] = useState<string | null>(null);
  const [player, setPlayer]     = useState<Player | null>(null);
  const [event, setEvent]       = useState<ClubEvent | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [games, setGames]       = useState<Game[]>([]);
  const [allEvents, setAllEvents] = useState<ClubEvent[]>([]);
  const [loading, setLoading]   = useState(true);
  const [clubLogo, setClubLogo] = useState<string>("");
  const [dark, setDark]         = useState(() => localStorage.getItem("spinloop_dark") === "1");
  // Teema on CSS-muuttujissa (src/theme.css); tämä vaihtaa arvot koko dokumentille.
  useEffect(() => { document.documentElement.dataset.theme = dark ? "dark" : "light"; }, [dark]);
  const toggleDark = () => setDark(d => { const n = !d; localStorage.setItem("spinloop_dark", n ? "1" : "0"); return n; });
  const [lang, setLang]         = useState<"fi"|"en">(() => (localStorage.getItem("spinloop_lang") as "fi"|"en") || "fi");
  const toggleLang = () => setLang(l => { const n = l === "fi" ? "en" : "fi"; localStorage.setItem("spinloop_lang", n); return n; });

  const T = dark ? {
    wrapBg:       "var(--sl-bg)",
    headerBg:     "var(--sl-header)",
    headerBorder: "1px solid rgba(255,255,255,0.08)",
    headerShadow: "0 2px 20px rgba(0,0,0,0.3)",
    logoColor:    "#ffffff",
    subtitleColor:"rgba(255,255,255,0.50)",
    nameColor:    "#ffffff",
    logoutColor:  "rgba(255,255,255,0.45)",
    navBg:        "var(--sl-header)",
    navBorder:    "1px solid rgba(255,255,255,0.08)",
    navShadow:    "0 -4px 24px rgba(0,0,0,0.4)",
    tabActive:    "var(--sl-accent)",
    tabInactive:  "rgba(255,255,255,0.40)",
  } : {
    wrapBg:       C.bg,
    headerBg:     "var(--sl-header)",
    headerBorder: `1px solid ${C.border}`,
    headerShadow: "0 2px 12px rgba(22,59,104,0.08)",
    logoColor:    C.navy,
    subtitleColor:C.muted,
    nameColor:    C.navy,
    logoutColor:  C.muted,
    navBg:        "var(--sl-header)",
    navBorder:    `1px solid ${C.border}`,
    navShadow:    "0 -4px 20px rgba(22,59,104,0.10)",
    tabActive:    C.navy,
    tabInactive:  C.muted,
  };

  // Lataa klubin logo
  useEffect(() => {
    getDoc(doc(db, "clubs", session.clubId)).then(snap => {
      const logo = snap.data()?.logoUrl || "";
      if (logo) setClubLogo(logo);
    }).catch(() => {});
  }, [session.clubId]);

  useEffect(() => {
    async function load() {
      if (!session.playerId) { setLoading(false); return; }

      // Lataa pelaajan profiili
      const pSnap = await getDoc(doc(db, "clubs", session.clubId, "players", session.playerId));
      if (pSnap.exists()) setPlayer({ id: pSnap.id, ...pSnap.data() } as Player);


      setLoading(false);
    }
    load();
  }, [session]);

  // Valitun tapahtuman tiedot, osallistujat ja pelit reaaliaikaisesti.
  useEffect(() => {
    if (!selectedEventId) { setEvent(null); setParticipants([]); setGames([]); return; }
    const base = ["clubs", session.clubId, "events", selectedEventId] as const;
    const unsub0 = onSnapshot(
      doc(db, ...base),
      snap => setEvent(snap.exists() ? ({ id: snap.id, ...snap.data() } as ClubEvent) : null)
    );
    const unsub1 = onSnapshot(
      collection(db, ...base, "participants"),
      snap => setParticipants(snap.docs.map(d => ({ id: d.id, ...d.data() } as Participant)))
    );
    const unsub2 = onSnapshot(
      collection(db, ...base, "games"),
      snap => setGames(snap.docs.map(d => ({ id: d.id, ...d.data() } as Game)))
    );
    return () => { unsub0(); unsub1(); unsub2(); };
  }, [selectedEventId, session.clubId]);

  // Klubin tapahtumat reaaliaikaisesti. Kertahaku jäi jumiin siihen
  // tilaan joka vallitsi sovellusta avattaessa: ylläpitäjän juuri luoma
  // tapahtuma ei ilmestynyt listalle, ja peruttu näkyi yhä.
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, "clubs", session.clubId, "events"), orderBy("createdAt", "desc")),
      snap => setAllEvents(snap.docs.map(d => ({ id: d.id, ...d.data() } as ClubEvent)))
    );
    return () => unsub();
  }, [session.clubId]);

  /**
   * Tulevat tapahtumat ilmoittautumislistalle.
   *
   * Pelkkä `status !== "finished"` ei riitä: tapahtuma jää tilaan "open"
   * jos ylläpitäjä unohtaa päättää sen, ja nousisi aikajärjestyksessä
   * listan kärkeen kuukausia myöhemmin elävän Ilmoittaudu-napin kanssa.
   * Käynnissä oleva näytetään silti, vaikka aloitusaika on mennyt.
   */
  const upcoming = React.useMemo(() => {
    const cutoff = Date.now() - 12 * 60 * 60 * 1000;
    return allEvents
      .filter(e => {
        if (e.status === "finished") return false;
        if (e.status === "active") return true;
        const t = e.datetime ? new Date(e.datetime).getTime() : NaN;
        return isNaN(t) || t >= cutoff;
      })
      .sort((a, b) => (a.datetime || "").localeCompare(b.datetime || ""));
  }, [allEvents]);

  // Oma osallistumistila jokaisesta tulevasta tapahtumasta. Haetaan vain
  // oma dokumentti, ei koko osallistujalistaa — se olisi kymmeniä turhia
  // lukuja per avaus.
  useEffect(() => {
    if (!session.playerId || upcoming.length === 0) return;
    // Vain kun lista on näkyvissä: näin paluu tapahtumanäkymästä hakee
    // tuoreen tilan eikä käyttäjä näe "Olet mukana" -korttia, jonka
    // ylläpitäjä on sillä välin poistanut.
    if (tab !== "event" || selectedEventId) return;
    let alive = true;
    Promise.all(upcoming.map(async ev => {
      try {
        const snap = await getDoc(
          doc(db, "clubs", session.clubId, "events", ev.id, "participants", session.playerId!)
        );
        return [ev.id, snap.exists() ? ({ id: snap.id, ...snap.data() } as Participant) : null] as const;
      } catch {
        return [ev.id, null] as const;
      }
    })).then(rows => {
      if (alive) setMyUpcoming(Object.fromEntries(rows));
    });
    return () => { alive = false; };
  }, [upcoming, session.clubId, session.playerId, tab, selectedEventId]);

  /**
   * Vahvistaa tai peruu oman osallistumisen.
   *
   * Peruminen jättää dokumentin paikalleen ja asettaa confirmed=false:
   * säännöt sallivat poiston vain ylläpitäjälle, ja ylläpitäjän on joka
   * tapauksessa hyvä nähdä kuka on perunut.
   */
  async function setAttendance(ev: ClubEvent, going: boolean) {
    if (!session.playerId || !session.playerName) return;
    setAttendBusy(ev.id);
    const ref = doc(db, "clubs", session.clubId, "events", ev.id, "participants", session.playerId);
    try {
      // Olemassaolo luetaan kannasta, ei myUpcoming-tilasta. Tila on tyhjä
      // siihen asti kunnes osallistumisten haku on valmis, mutta nappi on
      // näkyvissä jo sitä ennen — silloin ehtisi kirjoittaa paid:false ja
      // uuden createdAt:n vanhan, maksetun ilmoittautumisen päälle.
      const existing = (await getDoc(ref)).exists();
      await setDoc(ref, {
        playerId: session.playerId,
        displayName: session.playerName,
        confirmed: going,
        // Merkintä siitä että pelaaja kirjoitti tämän itse. Ylläpitäjän
        // ilmoitus (sendParticipantPush) laukeaa vain tästä: ilman sitä
        // hänen oma kahdentoista pelaajan lisäyksensä lähettäisi
        // kaksitoista ilmoitusta hänelle itselleen.
        source: "player",
        // createdAt ja paid vain ensimmäisellä kerralla. merge kirjoittaisi
        // createdAt:n uusiksi joka napautuksella, jolloin ylläpitäjän
        // osallistujalista menisi ilmoittautumisjärjestykseen nähden sekaisin,
        // ja paid nollautuisi maksetusta maksamattomaksi.
        ...(existing ? {} : { paid: false, createdAt: new Date().toISOString() }),
        updatedAt: new Date().toISOString(),
      }, { merge: true });
      setMyUpcoming(m => ({
        ...m,
        [ev.id]: {
          id: session.playerId!,
          playerId: session.playerId,
          displayName: session.playerName!,
          confirmed: going,
          paid: m[ev.id]?.paid ?? false,
        },
      }));
    } catch {
      alert(lang === "fi"
        ? "Osallistumisen tallennus epäonnistui. Tarkista verkkoyhteys."
        : "Could not save your attendance. Check your connection.");
    }
    setAttendBusy(null);
  }

  // Laske tilastot kaikista tapahtumista
  const stats = React.useMemo(() => {
    if (!session.playerName) return null;
    const name = session.playerName.toLowerCase();
    let wins = 0, losses = 0, gamesPlayed = 0;

    allEvents.forEach(ev => {
      // Käy läpi pelit (tarvitsee erillisen haun — yksinkertaistetaan tässä)
    });
    return { wins, losses, gamesPlayed };
  }, [allEvents, session.playerName]);

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
      <div style={{ fontSize: 14, color: C.muted }}>{lang === "fi" ? "Ladataan..." : "Loading..."}</div>
    </div>
  );

  const myParticipant = participants.find(p =>
    p.playerId === session.playerId || p.displayName?.toLowerCase() === session.playerName?.toLowerCase()
  );

  const tabs = [
    { key: "event",   icon: "📅", label: lang === "fi" ? "Tapahtumat" : "Events" },
    ...(activeSeason ? [{ key: "league", icon: "📊", label: lang === "fi" ? "Liiga" : "League" }] : []),
    { key: "news",    icon: "📰", label: lang === "fi" ? "Uutiset" : "News" },
    { key: "profile", icon: "👤", label: lang === "fi" ? "Profiili" : "Profile" },
    { key: "history", icon: "🏆", label: lang === "fi" ? "Historia" : "History" },
    // Järjestäjälle oma välilehti; rooli tulee palvelimelta (docs/roolit.md).
    ...(session.role === "organizer" ? [{ key: "organize", icon: "📋", label: lang === "fi" ? "Järjestä" : "Organize" }] : []),
  ] as { key: PlayerTab; icon: string; label: string }[];

  return (
    <div style={{ background: T.wrapBg, minHeight: "100vh", maxWidth: 520, margin: "0 auto", fontFamily: F.body, position: "relative" }}>
      {/* Court pattern */}
      <div style={{
        position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0,
        backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(courtPattern)}")`,
        backgroundRepeat: "repeat", backgroundSize: "400px",
      }} />
      {/* Pallo vesileimana oikeassa alakulmassa, osin ruudun ulkopuolella.
          Keskellä se kilpaili sisällön kanssa; kulmassa se on tunnelma, ei
          tausta. Kiinnitetty, jotta se ei vieri sisällön mukana. */}
      <img src="/icons/pickleball.png" alt="" aria-hidden="true" style={{
        position: "fixed", right: -140, bottom: -120, width: 460, height: 460,
        opacity: dark ? 0.10 : 0.08, transform: "rotate(-14deg)",
        pointerEvents: "none", zIndex: 0, userSelect: "none",
      }} />

      {/* Sticky header */}
      <div style={{
        position: "sticky", top: 0, zIndex: 100,
        background: T.headerBg,
        borderBottom: T.headerBorder,
        boxShadow: T.headerShadow,
        padding: "10px 20px",
        // iOS: Dynamic Island peittaisi palkin ilman turva-aluetta
        paddingTop: "calc(10px + env(safe-area-inset-top))",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        {/* Vasen: SPINLOOP ja ajossa oleva versio */}
        <div style={{ minWidth: 80 }}>
          <div style={{ fontSize: 22, fontWeight: 800, fontFamily: F.heading, lineHeight: 1, color: T.logoColor }}>
            <Wordmark />
          </div>
          {/* Versio yläkulmassa, jotta "onko tämä se uusi" ei vaadi
              sivun vierittämistä pohjaan asti. Alalaidan versiokortti
              jää ennalleen — siinä on myös yhtiö ja tietosuojalinkki. */}
          <VersionTag lang={lang} color={T.subtitleColor} style={{ display: "block", marginTop: 3 }} />
        </div>

        {/* Keskellä: klubin logo */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <img
            src={clubLogo || "/logo192.png"}
            alt={session.clubName}
            style={{ width: 52, height: 52, borderRadius: 12, objectFit: "contain" }}
          />
          <div style={{ fontSize: 10, color: T.subtitleColor, marginTop: 2, fontFamily: F.body }}>
            {session.clubName}
          </div>
        </div>

        {/* Oikea: teema-toggle + pelaajan nimi + logout */}
        <div style={{ textAlign: "right", minWidth: 80 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6, marginBottom: 2 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.nameColor, fontFamily: F.heading }}>{session.playerName}</div>
            <button onClick={toggleDark} style={{ fontSize: 14, background: "none", border: "none", cursor: "pointer", padding: 0, lineHeight: 1 }} title="Vaihda teema">
              {dark ? "☀️" : "🌙"}
            </button>
            <button onClick={toggleLang} style={{ fontSize: 14, background: "none", border: "none", cursor: "pointer", padding: 0, lineHeight: 1 }} title="Language">
              {lang === "fi" ? "🌐 EN" : "🌐 FI"}
            </button>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={() => setShowHelp(true)} style={{ fontSize: 10, color: T.logoutColor, background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: F.body }}>
              {lang === "fi" ? "Ohjeet" : "Help"}
            </button>
            <button onClick={onLogout} style={{ fontSize: 10, color: T.logoutColor, background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: F.body }}>
              {lang === "fi" ? "Kirjaudu ulos" : "Sign out"}
            </button>
          </div>
        </div>
      </div>

      {/* Sisältö */}
      {/*
        Ei zIndexia tahan. `position: relative` + `zIndex` luo oman
        pinoamiskontekstin, ja silloin kaikki sisalla olevat z-indeksit ovat
        suhteessa vain siihen: valilehden sisalla avautuva modaali (zIndex 300)
        jai ylapalkin ja alanavigaation (zIndex 100) alle, koska kaare itse oli
        tasolla 1. Ilman zIndexia `relative` ei luo kontekstia, ja modaali
        kilpailee palkkien kanssa samalla tasolla kuten on tarkoitus.

        Sisalto pysyy silti kenttakuvion (zIndex 0, kiinnitetty) paalla:
        z-index:auto ja z-index:0 maalataan samassa vaiheessa DOM-jarjestyksessa,
        ja tama kaare on kuviota myohemmin.
      */}
      <div style={{ position: "relative", padding: "16px 16px calc(90px + var(--sl-safe-bottom))" }}>
        {tab === "event" && (
          selectedEventId && event ? (
            <>
              <button
                onClick={() => setSelectedEventId(null)}
                style={{
                  background: "none", border: "none", cursor: "pointer", padding: 0,
                  marginBottom: 12, fontFamily: F.body, fontSize: 13, fontWeight: 700,
                  color: dark ? "rgba(255,255,255,0.65)" : C.muted,
                }}
              >
                ← {lang === "fi" ? "Kaikki tapahtumat" : "All events"}
              </button>
              <EventTab
                event={event} participants={participants} games={games}
                myParticipant={myParticipant} clubId={session.clubId}
                eventId={selectedEventId} myName={session.playerName || ""} lang={lang}
                season={seasonOf(event?.seasonId)} myPlayerId={session.playerId}
              />
              <EventMessages
                clubId={session.clubId} eventId={selectedEventId} role={session.role}
                myPlayerId={session.playerId} myName={session.playerName}
                clubName={session.clubName} lang={lang} dark={dark} mode={event.messages}
              />
            </>
          ) : (
            <UpcomingEventsTab
              events={upcoming}
              mine={myUpcoming}
              myName={session.playerName}
              busyId={attendBusy}
              canAttend={!!session.playerId}
              dark={dark}
              lang={lang}
              onOpen={id => setSelectedEventId(id)}
              onAttend={setAttendance}
              seasonNameOf={id => seasonOf(id)?.name}
            />
          )
        )}
        {tab === "league" && activeSeason && (
          <LeagueTab clubId={session.clubId} season={activeSeason} myPlayerId={session.playerId} myName={session.playerName} lang={lang} />
        )}
        {tab === "news" && (
          <div>
            <AnnouncementsList clubId={session.clubId} lang={lang} />
            <NewsFeed lang={lang} />
          </div>
        )}
        {tab === "profile" && <ProfileTab player={player} session={session} lang={lang} />}
        {tab === "history" && <HistoryTab clubId={session.clubId} playerId={session.playerId} playerName={session.playerName} lang={lang} />}
        {tab === "organize" && session.role === "organizer" && <OrganizerTab clubId={session.clubId} lang={lang} />}
      </div>

      {showHelp && <HelpGuide role={session.role} lang={lang} onClose={closeHelp} />}
      {showReferee && <RefereeAsk lang={lang} onClose={closeReferee} />}
      {!welcomeDone && player && session.playerId && !player.onboardedAt && (
        <Welcome
          clubId={session.clubId}
          playerId={session.playerId}
          playerName={session.playerName || player.name}
          clubName={session.clubName}
          lang={lang}
          onDone={() => setWelcomeDone(true)}
          onOpenProfile={() => { setWelcomeDone(true); setTab("profile"); }}
        />
      )}

      {/* Bottom navigation */}
      <div style={{
        position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)",
        width: "100%", maxWidth: 520, zIndex: 100,
        background: T.navBg, borderTop: T.navBorder,
        boxShadow: T.navShadow,
        display: "flex",
        // Järjestelmäpainikkeiden / kotipalkin alue (theme.css).
        paddingBottom: "var(--sl-safe-bottom)",
      }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            flex: 1, margin: "6px 3px 10px", padding: "7px 0 6px", border: "none", cursor: "pointer",
            borderRadius: 14, background: tab === t.key ? "var(--sl-soft)" : "none",
            display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
          }}>
            <span style={{ fontSize: 22, lineHeight: 1 }}>{t.icon}</span>
            <span style={{
              fontSize: 10, fontWeight: tab === t.key ? 700 : 600, fontFamily: F.body,
              color: tab === t.key ? T.tabActive : T.tabInactive,
            }}>{t.label}</span>
          </button>
        ))}
        {/* Tuomari ei ole valilehti vaan avaa nakyman, joten se ei saa
            aktiivista taustaa. Kuvake 22 px kuten muidenkin emojit, jotta
            rivi pysyy tasaisena. */}
        <button onClick={() => setShowReferee(true)} style={{
          flex: 1, margin: "6px 3px 10px", padding: "7px 0 6px", border: "none", cursor: "pointer",
          borderRadius: 14, background: "none",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
        }}>
          <RefereeMark size={22} />
          <span style={{
            fontSize: 10, fontWeight: 600, fontFamily: F.body, color: T.tabInactive,
          }}>{lang === "fi" ? "Tuomari" : "Referee"}</span>
        </button>
      </div>
    </div>
  );
}

// ── Kalenteri-apufunktiot ─────────────────────────────────────

// ── UpcomingEventsTab ─────────────────────────────────────────
//
// Pelaajan oma ilmoittautumisnakyma. Tama puuttui kokonaan: ennen
// Tapahtuma-valilehti avautui vain siihen yhteen tapahtumaan, jonka
// PIN-koodilla oli kirjauduttu, eika klubin muita tapahtumia nakynyt
// missaan. Kutsulinkilla kirjautunut ei paassyt vahvistamaan
// osallistumistaan lainkaan.

// Sama kartta kuin AdminView'ssa ja kutsusahkopostissa. Erillinen
// switch oli paatynyt vaarinpain: padel sai tennispallon ja pickleball
// mailan.
const SPORT_EMOJI: Record<string, string> = {
  pickleball: "\u{1F3BE}", padel: "\u{1F3D3}", tennis: "\u{1F3BE}", squash: "\u{1F7E1}",
};
function eventSportEmoji(sport: Sport): string {
  return SPORT_EMOJI[sport] || "\u{1F3BE}";
}

function UpcomingEventsTab({ events, mine, myName, busyId, canAttend, dark, lang, onOpen, onAttend, seasonNameOf }: {
  seasonNameOf?: (id?: string) => string | undefined;
  events: ClubEvent[];
  mine: Record<string, Participant | null>;
  myName?: string;
  busyId: string | null;
  canAttend: boolean;
  dark: boolean;
  lang: "fi" | "en";
  onOpen: (id: string) => void;
  onAttend: (ev: ClubEvent, going: boolean) => void;
}) {
  const cardBg = dark
    ? { background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.10)" }
    : { background: C.card, border: `1px solid ${C.border}` };
  const titleColor = dark ? "#ffffff" : C.text;
  const subColor   = dark ? "rgba(255,255,255,0.55)" : C.muted;

  if (events.length === 0) {
    return (
      <div style={{ ...card, ...cardBg, textAlign: "center", padding: "36px 20px" }}>
        <div style={{ fontSize: 34, marginBottom: 10 }}>{"\u{1F4C5}"}</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: titleColor, fontFamily: F.heading, marginBottom: 6 }}>
          {lang === "fi" ? "Ei tulevia tapahtumia" : "No upcoming events"}
        </div>
        <div style={{ fontSize: 12, color: subColor, lineHeight: 1.6 }}>
          {lang === "fi"
            ? "Kun yll\u00e4pit\u00e4j\u00e4 luo tapahtuman, se ilmestyy t\u00e4h\u00e4n ja voit ilmoittautua yhdell\u00e4 napautuksella."
            : "When an organiser creates an event it appears here and you can sign up with one tap."}
        </div>
      </div>
    );
  }

  return (
    <div>
      {events.map(ev => {
        const me      = mine[ev.id];
        const status  = attendanceStatus(ev, me, myName);
        const going   = status === "going";
        const busy    = busyId === ev.id;
        const when    = ev.datetime ? new Date(ev.datetime) : null;
        const dateStr = when
          ? when.toLocaleString(lang === "fi" ? "fi-FI" : "en-GB", {
            weekday: "short", day: "numeric", month: "numeric",
            hour: "2-digit", minute: "2-digit",
          })
          : "";
        return (
          <div key={ev.id} style={{ ...card, ...cardBg }}>
            <button
              onClick={() => onOpen(ev.id)}
              style={{
                display: "block", width: "100%", textAlign: "left",
                background: "none", border: "none", padding: 0, cursor: "pointer",
                fontFamily: F.body,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                {/* Pickleballille oma pallokuva (sama kuin kenttäkortissa); Unicodessa
                    ei ole pickleball-emojia ja 🎾 on tennispallo. */}
                {(ev.sport ?? "pickleball") === "pickleball"
                  ? <img src="/icons/pickleball.png" alt="" aria-hidden="true" style={{ width: 22, height: 22, flexShrink: 0, objectFit: "contain" }} />
                  : <span style={{ fontSize: 18 }}>{eventSportEmoji(ev.sport)}</span>}
                <span style={{ fontSize: 15, fontWeight: 800, color: titleColor, fontFamily: F.heading }}>
                  {ev.title || ev.venue || (lang === "fi" ? "Tapahtuma" : "Event")}
                </span>
                {ev.status === "active" && (
                  <span style={{
                    fontSize: 9, fontWeight: 800, color: "#fff", background: C.accent,
                    borderRadius: 999, padding: "2px 7px", letterSpacing: 0.5,
                  }}>
                    {lang === "fi" ? "K\u00c4YNNISS\u00c4" : "LIVE"}
                  </span>
                )}
                {ev.seasonId && seasonNameOf?.(ev.seasonId) && (
                  <span title={seasonNameOf(ev.seasonId)} style={{
                    fontSize: 9, fontWeight: 800, color: C.navy, background: `var(--sl-accent-a20)`,
                    borderRadius: 999, padding: "2px 7px", letterSpacing: 0.5,
                  }}>
                    🏆 {lang === "fi" ? "LIIGA" : "LEAGUE"}
                  </span>
                )}
                {status === "invited" && (
                  <span style={{
                    fontSize: 9, fontWeight: 800, color: "#fff", background: C.navy,
                    borderRadius: 999, padding: "2px 7px", letterSpacing: 0.5,
                  }}>
                    📩 {lang === "fi" ? "KUTSU" : "INVITED"}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, color: subColor, lineHeight: 1.6 }}>
                {dateStr}
                {ev.venue && ev.title ? ` \u00b7 ${ev.venue}` : ""}
                {ev.panos ? ` \u00b7 ${ev.panos} \u20ac` : ""}
              </div>
            </button>

            {status === "invited" && canAttend && (
              <div style={{ fontSize: 12, fontWeight: 700, color: titleColor, marginTop: 10 }}>
                {lang === "fi" ? "Sinut on kutsuttu. Tuletko?" : "You're invited. Are you coming?"}
              </div>
            )}
            {status === "declined" && (
              <div style={{ fontSize: 12, color: subColor, marginTop: 10 }}>
                {lang === "fi" ? "Ilmoitit, ettet pääse." : "You said you can't make it."}
              </div>
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              {canAttend && status === "invited" ? (
                <>
                  <button onClick={() => onAttend(ev, true)} disabled={busy} style={{
                    ...btn, flex: 1, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1,
                    background: C.navy, color: C.white, border: "none",
                    fontWeight: 700, fontFamily: F.body, fontSize: 13,
                  }}>
                    {lang === "fi" ? "✓ Tulen" : "✓ I'm in"}
                  </button>
                  <button onClick={() => onAttend(ev, false)} disabled={busy} style={{
                    ...btn, flex: 1, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1,
                    background: "none", color: titleColor,
                    border: `1px solid ${dark ? "rgba(255,255,255,0.16)" : C.border}`,
                    fontWeight: 700, fontFamily: F.body, fontSize: 13,
                  }}>
                    {lang === "fi" ? "En pääse" : "Can't make it"}
                  </button>
                </>
              ) : canAttend ? (
                <button
                  onClick={() => onAttend(ev, !going)}
                  disabled={busy}
                  style={{
                    ...btn, flex: 1, cursor: busy ? "wait" : "pointer",
                    opacity: busy ? 0.6 : 1,
                    background: going ? "rgba(46,204,113,0.12)" : C.navy,
                    color: going ? "#0f8a4d" : C.white,
                    border: going ? `1px solid ${C.accent}` : "none",
                    fontWeight: 700, fontFamily: F.body, fontSize: 13,
                  }}
                >
                  {going
                    ? (lang === "fi" ? "\u2713 Olet mukana" : "\u2713 You're in")
                    : (lang === "fi" ? "Ilmoittaudu" : "Sign up")}
                </button>
              ) : (
                <div style={{ flex: 1, fontSize: 11, color: subColor, lineHeight: 1.5 }}>
                  {lang === "fi"
                    ? "Ilmoittautuminen vaatii pelaajaprofiilin."
                    : "Signing up requires a player profile."}
                </div>
              )}
              <button
                onClick={() => onOpen(ev.id)}
                style={{
                  ...btn, background: "none", color: subColor,
                  border: `1px solid ${dark ? "rgba(255,255,255,0.16)" : C.border}`,
                  fontWeight: 700, fontFamily: F.body, fontSize: 13, cursor: "pointer",
                }}
              >
                {lang === "fi" ? "Avaa" : "Open"}
              </button>
            </div>

            {going && (
              <div style={{ fontSize: 11, color: subColor, marginTop: 8 }}>
                {lang === "fi"
                  ? "Napauta uudelleen peruaksesi."
                  : "Tap again to cancel."}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── EventTab ──────────────────────────────────────────────────
function EventTab({ event, participants, games, myParticipant, clubId, eventId, myName, lang, season, myPlayerId }: {
  season?: Season | null; myPlayerId?: string;
  event: ClubEvent | null;
  participants: Participant[];
  games: Game[];
  myParticipant?: Participant;
  clubId: string;
  eventId: string;
  myName: string;
  lang: "fi"|"en";
}) {
  // Osallistujadokumentissa on vain nimi, joten kuva haetaan pelaajan
  // tunnuksella. PlayerView ei muuten lataa klubin pelaajia, joten tämä
  // on yksi lisähaku näkymää kohti. Klubit ovat kymmeniä pelaajia, joten
  // se ei ole mitattavissa; reaaliaikaista kuuntelijaa ei tarvita, koska
  // kuva vaihtuu harvoin.
  // Kentän päät, jos pelipaikalle on nimetty ne. Pelaajalle tämä on se
  // kohta jossa tasauksesta tulee hyötyä: hän näkee kumpaan päätyyn
  // mennään eikä joudu sopimaan siitä kentän laidalla.
  //
  // Koukku on ennen `if (!event)` -poistumista, koska koukkuja ei saa
  // kutsua ehdollisesti (react-hooks/rules-of-hooks).
  const sides = sideNames(useClubVenues(clubId), event?.venue);
  const [photoById, setPhotoById] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    if (!clubId) return;
    let alive = true;
    getDocs(collection(db, "clubs", clubId, "players")).then(snap => {
      if (!alive) return;
      const m = new Map<string, string>();
      snap.docs.forEach(d => {
        const url = (d.data() as { photoUrl?: string }).photoUrl;
        if (url) m.set(d.id, url);
      });
      setPhotoById(m);
    }).catch(() => undefined);
    return () => { alive = false; };
  }, [clubId]);

  // Lipas-koodit luetaan klubidokumentista. Aiemmin ne luettiin
  // localStoragesta, jonne vain ylläpitäjä oli ne omassa selaimessaan
  // kirjoittanut — pelaajalla arvo oli aina tyhjä, joten maksunappia ei
  // näytetty kenellekään koskaan.
  const [lipasPanos, setLipasPanos] = useState("");
  const [lipasSali,  setLipasSali]  = useState("");
  useEffect(() => {
    let alive = true;
    getDoc(doc(db, "clubs", clubId)).then(snap => {
      if (!alive) return;
      const d = snap.data() || {};
      setLipasPanos(d.lipasPanos || "");
      setLipasSali(d.lipasSali || "");
    }).catch(() => undefined);
    return () => { alive = false; };
  }, [clubId]);
  // Palauttaa null jos arvosta ei saa toimivaa linkkiä. Aiemmin tässä oli
  // kolmas haara `mobilepay://lipas/<koodi>`, joka rakennettiin lyhyestä
  // Lipas-koodista. Se ei toimi: MobilePay vastaa "Tuntematon QR-koodi,
  // QR-koodia ei ole aktivoitu". Toimiva arvo on lippaan oma jakolinkki.
  const panosLink = mobilePayLink(lipasPanos);
  const saliLink  = mobilePayLink(lipasSali);
  if (!event) return <div style={{ textAlign: "center", padding: 40, color: C.muted }}>{lang === "fi" ? "Tapahtumaa ei löydy." : "Event not found."}</div>;

  // Pistetaulukko on yhteinen komponentti ylläpitäjän kanssa
  // (components/NightStandings.tsx). Tässä oli oma toteutus, joka näytti
  // voitot ja kutsui niitä pisteiksi — 1.11.2026 illassa pelaaja näki
  // "3 pts" neljä kertaa, kun oikeat pisteet olivat 52, 50, 48 ja 46.
  // Ylläpitäjän näkymä näytti saman illan oikein, eli sama ilta kertoi
  // kahdelle ihmiselle eri tarinan.
  //
  // Taulukko vaatii vähintään yhden ratkenneen ottelun. Ilman sitä kaikki
  // ovat tasoissa 0–0, jaettu sija antaa kaikille sijan 1, ja jokainen
  // saisi kultamitalin ennen kuin yhtään palloa on lyöty.
  const osallistujaNimet = participants.map(p => p.displayName);

  // Omat ottelut kierrosjarjestyksessa. Nimivertailu trimmaa ja
  // pienentaa, koska osallistujalistan ja ottelun nimi voivat erota
  // valilyonnilla — sama syy kuin LoginFlow'n normName-funktiossa.
  const same = (a: string, b: string) =>
    a.trim().toLowerCase() === b.trim().toLowerCase();
  const myMatches = games
    .filter(g => g.team1.some(n => same(n, myName)) || g.team2.some(n => same(n, myName)))
    .sort((a, b) => (a.round ?? 0) - (b.round ?? 0))
    .map(g => {
      const inT1 = g.team1.some(n => same(n, myName));
      return {
        g,
        myTeam: inT1 ? g.team1 : g.team2,
        oppTeam: inT1 ? g.team2 : g.team1,
        myScore: inT1 ? g.score1 : g.score2,
        oppScore: inT1 ? g.score2 : g.score1,
        result: gameResult(g),
        mine: (inT1 ? "team1" : "team2") as "team1" | "team2",
      };
    });

  return (
    <div>
      {/* Tapahtumakortti: ylhäältä kuvattu kenttä — keittiöalue, verkko ja pallo */}
      <div style={{
        ...card, position: "relative", overflow: "hidden", border: "none",
        background: C.navy, color: C.white, padding: "18px 16px 16px",
        boxShadow: "0 12px 28px var(--sl-primary-a20)",
      }}>
        {/* Kenttä piirretään tekstin ulkopuolelle: keittiöalue alaosaan, verkko
            kortin alareunaan ja keskiviiva oikeaan laitaan, jotta nimi ja
            päivämäärä pysyvät luettavina. */}
        <div style={{ position: "absolute", left: 0, right: 0, top: "58%", height: "22%", background: C.accent, opacity: 0.45 }} />
        <div style={{ position: "absolute", left: 0, right: 0, top: "80%", borderTop: "3px dashed rgba(255,255,255,0.6)" }} />
        <div style={{ position: "absolute", inset: 0, opacity: 0.35, background: "linear-gradient(90deg, transparent 71.3%, rgba(255,255,255,0.7) 71.3%, rgba(255,255,255,0.7) 72.7%, transparent 72.7%), linear-gradient(0deg, transparent 57%, rgba(255,255,255,0.7) 57%, rgba(255,255,255,0.7) 58.2%, transparent 58.2%)" }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, rgba(8,28,52,0.42) 0%, rgba(8,28,52,0.18) 55%, transparent 75%)" }} />
        <img src="/icons/pickleball.png" alt="" style={{ position: "absolute", right: -16, top: -16, width: 124, height: 124, transform: "rotate(-12deg)", filter: "drop-shadow(0 12px 18px rgba(0,0,0,0.35))", pointerEvents: "none" }} />
        <div style={{ position: "relative", display: "flex", flexDirection: "column", gap: 4, textAlign: "left" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", paddingRight: 92 }}>
            <span style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, opacity: 0.85 }}>{event.sport} Americano</span>
            {season && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 999, background: C.accent, color: C.white }}>🏆 {season.name}</span>
            )}
          </div>
          <div style={{ fontFamily: F.heading, fontWeight: 700, fontSize: 28, lineHeight: 1, letterSpacing: "0.02em", marginTop: 4, paddingRight: 92, textShadow: "0 1px 2px rgba(0,0,0,0.35)" }}>
            {event.venue || (lang === "fi" ? "Tapahtuma" : "Event")}
          </div>
          {event.venueAddress && (
            // Osoite karttalinkkinä: avaa laitteen oletuskartan hakuna, joten
            // sama linkki toimii iOS:lla, Androidilla ja selaimessa.
            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.venueAddress)}`}
              target="_blank" rel="noopener noreferrer"
              style={{ color: C.white, fontSize: 12, opacity: 0.92, textDecoration: "underline" }}>
              {event.venueAddress}
            </a>
          )}
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 6, flexWrap: "wrap", textShadow: "0 1px 2px rgba(0,0,0,0.35)" }}>
            <span style={{ fontFamily: F.heading, fontWeight: 700, fontSize: 22, lineHeight: 1, color: C.gold }}>
              {new Date(event.datetime).toLocaleString("fi-FI", { hour: "2-digit", minute: "2-digit" }).replace(".", ":")}
            </span>
            <span style={{ fontSize: 14, fontWeight: 700 }}>
              {new Date(event.datetime).toLocaleString(lang === "fi" ? "fi-FI" : "en-GB", { weekday: "long", day: "numeric", month: "long" })}
            </span>
          </div>
          {myParticipant?.confirmed && (
            <div style={{ marginTop: 10 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, padding: "7px 12px", borderRadius: 999, background: C.accent, color: C.white }}>
                ✓ {lang === "fi" ? "Olet mukana" : "You're in"}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Liigailta: säännöt ja oma tilanne kaudessa */}
      {season && (
        <LeagueEventCard clubId={clubId} season={season} myPlayerId={myPlayerId} myName={myName} lang={lang} />
      )}

      {/* Lisää kalenteriin */}
      <div style={{ marginBottom: 14 }}><CalendarButtons event={event} lang={lang} /></div>

      {/* Panos + MobilePay */}
      {event.panos != null && event.panos > 0 && (
        <div style={{ ...card, background: "rgba(244,164,38,0.06)", border: "1px solid rgba(244,164,38,0.25)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: panosLink ? 12 : 0 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.gold, textTransform: "uppercase", letterSpacing: "0.08em" }}>🎯 {lang === "fi" ? "Pelipanos" : "Entry fee"}</div>
              <div style={{ fontSize: 28, fontWeight: 900, color: C.gold, fontFamily: F.heading, fontStyle: "italic", marginTop: 2 }}>{event.panos} €</div>
            </div>
            {myParticipant?.paid ? (
              <span style={{ fontSize: 13, fontWeight: 700, color: "#059669", background: "#d1fae5", borderRadius: 10, padding: "8px 14px" }}>{lang === "fi" ? "✓ Maksettu" : "✓ Paid"}</span>
            ) : (
              <span style={{ fontSize: 12, color: C.muted, background: C.soft, borderRadius: 8, padding: "6px 12px" }}>{lang === "fi" ? "Maksu avoin" : "Payment pending"}</span>
            )}
          </div>
          {panosLink && !myParticipant?.paid && (
            <button onClick={() => { window.open(panosLink, "_blank"); }} style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              padding: "13px 0", borderRadius: 12, border: "none", width: "100%",
              background: "linear-gradient(135deg, #5a3fd8, #3b82f6)",
              color: "#fff", fontWeight: 700, fontSize: 14, fontFamily: F.heading, letterSpacing: 0.5,
              boxShadow: "0 4px 16px rgba(90,63,216,0.3)", cursor: "pointer",
            }}>
              <span style={{ fontSize: 20 }}>📱</span> {lang === "fi" ? "Maksa MobilePay" : "Pay with MobilePay"} — {event.panos} €
            </button>
          )}
        </div>
      )}

      {/* Salivuoro MobilePay */}
      {saliLink && (
        <button onClick={() => { window.open(saliLink, "_blank"); }} style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          padding: "13px 0", borderRadius: 12, border: "none", width: "100%", marginBottom: 14,
          background: "linear-gradient(135deg, #0369a1, #38bdf8)",
          color: "#fff", fontWeight: 700, fontSize: 14, fontFamily: F.heading, letterSpacing: 0.5,
          boxShadow: "0 4px 16px rgba(3,105,161,0.25)", cursor: "pointer",
        }}>
          <span style={{ fontSize: 20 }}>🏟️</span> {lang === "fi" ? "Maksa Salivuoro MobilePay" : "Pay Court Fee MobilePay"}
        </button>
      )}

      {/* Pelaajat */}
      <div style={card}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
          {lang === "fi" ? "Pelaajat" : "Players"} ({participants.length})
        </div>
        {participants.map((p, i) => (
          <div key={p.id} style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "8px 10px", background: C.soft, borderRadius: 8, marginBottom: 6,
          }}>
            <Avatar
              name={p.displayName} photoUrl={photoById.get(p.playerId || "")}
              size={28} fontSize={11}
              background={p.confirmed ? C.navy : C.muted} color={C.white}
            />
            <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: C.text }}>{p.displayName}</span>
            {p.confirmed && <span style={{ fontSize: 10, color: "#059669" }}>✓</span>}
          </div>
        ))}
      </div>

      {/* Omat ottelut — nakyy heti kun arvonta on tehty.
          Pistetaulukko alla nayttaa vasta kirjatut tulokset, joten ennen
          tata pelaaja ei nahnyt lainkaan kenen kanssa ja ketä vastaan han
          pelaa. Sama tieto oli olemassa vain HistoryTabissa, eli vasta
          illan paattymisen jalkeen. */}
      {myMatches.length > 0 && (
        <div style={card}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
            <PickleballBall size={14} /> {lang === "fi" ? "Omat ottelusi" : "Your matches"}
          </div>
          {sides && (
            <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5, marginBottom: 10 }}>
              {lang === "fi"
                ? "Päät jaetaan tasaisesti, joten kukaan ei pelaa koko iltaa samassa päässä."
                : "The ends are shared evenly, so nobody plays the whole evening at the same end."}
            </div>
          )}
          {myMatches.map(({ g, myTeam, oppTeam, myScore, oppScore, result, mine }) => {
            const played = result !== "unplayed";
            const won = played && result === mine;
            const tie = result === "tie";
            // Pelaamaton ottelu on neutraali: vihrea tai punainen vaittaisi
            // tulosta jota ei ole.
            const bg = !played ? C.soft : tie ? C.soft : won ? "#f0fdf4" : "#fef2f2";
            const border = !played ? C.border : tie ? C.border : won ? "#bbf7d0" : "#fecaca";
            return (
              <div key={g.id} style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "8px 10px", borderRadius: 8, marginBottom: 6,
                background: bg, border: `1px solid ${border}`,
              }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, width: 26, textAlign: "center" }}>
                  {g.round ?? "–"}.
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>
                    {myTeam.join(" & ")}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted }}>vs {oppTeam.join(" & ")}</div>
                  {sides && (
                    <div style={{ fontSize: 10, color: C.muted, marginTop: 2, fontWeight: 600 }}>
                      ⇄ {mine === "team1" ? sides.a : sides.b}
                    </div>
                  )}
                </div>
                {played ? (
                  <div style={{
                    fontSize: 16, fontWeight: 900, fontFamily: "monospace",
                    color: tie ? C.text : won ? "#16a34a" : "#dc2626",
                  }}>
                    {myScore}–{oppScore}
                  </div>
                ) : (
                  <span style={{ fontSize: 11, color: C.muted }}>
                    {lang === "fi" ? "ei tulosta" : "no result"}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pistetaulukko */}
      {nightHasResults(games, osallistujaNimet) && (
        <div style={card}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
            🏆 {lang === "fi" ? "Pistetaulukko" : "Leaderboard"}
          </div>
          <NightStandings games={games} names={osallistujaNimet} lang={lang} myName={myName} />
        </div>
      )}

      {/* Haastapelit */}
      <ChallengeGames clubId={clubId} eventId={eventId} myName={myName} participants={participants} sport={event.sport} lang={lang} />
    </div>
  );
}

// ── ChallengeGames — vapaat lisäpelit ─────────────────────────
function ChallengeGames({ clubId, eventId, myName, participants, sport, lang }: {
  clubId: string; eventId: string; myName: string; participants: Participant[]; sport: Sport; lang: "fi"|"en";
}) {
  const [challenges, setChallenges] = useState<any[]>([]);
  const [showForm, setShowForm]     = useState(false);
  const [team1, setTeam1]           = useState<string[]>([]);
  const [team2, setTeam2]           = useState<string[]>([]);
  const [score1, setScore1]         = useState("");
  const [score2, setScore2]         = useState("");
  const [saving, setSaving]         = useState(false);

  const names = participants.map(p => p.displayName).filter(Boolean);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "clubs", clubId, "events", eventId, "challenges"),
      snap => setChallenges(snap.docs.map(d => ({ id: d.id, ...d.data() })).reverse())
    );
    return unsub;
  }, [clubId, eventId]);

  function togglePlayer(name: string, team: 1 | 2) {
    const set  = team === 1 ? team1 : team2;
    const other = team === 1 ? team2 : team1;
    const setFn = team === 1 ? setTeam1 : setTeam2;
    if (other.includes(name)) return; // jo toisessa joukkueessa
    if (set.includes(name)) setFn(set.filter(n => n !== name));
    else if (set.length < 2) setFn([...set, name]);
  }

  async function saveChallenge() {
    if (team1.length === 0 || team2.length === 0) return;
    if (score1 === "" || score2 === "") return;
    setSaving(true);
    try {
      const { doc: fiDoc, setDoc: fiSet, getDoc: fiGet, updateDoc: fiUpdate } = await import("firebase/firestore");
      const s1 = Number(score1), s2 = Number(score2);
      const id = `ch_${Date.now()}`;
      await fiSet(fiDoc(db, "clubs", clubId, "events", eventId, "challenges", id), {
        team1, team2, score1: s1, score2: s2,
        sport,
        createdBy: myName,
        createdAt: new Date().toISOString(),
      });

      // ── Päivitä DUPR jokaiselle pelaajalle ──────────────────
      const winners = s1 > s2 ? team1 : team2;
      const losers  = s1 > s2 ? team2 : team1;
      const ratingKey = sport === "pickleball" ? "ratings.pickleball"
                      : sport === "padel"       ? "ratings.padel"
                      : sport === "tennis"      ? "ratings.tennis"
                      : "ratings.pickleball";
      const CLAMP = (v: number) => Math.max(2.0, Math.min(8.0, v));

      const allPlayers = [...winners.map(n => ({ name: n, delta: +0.075 })),
                          ...losers.map(n =>  ({ name: n, delta: -0.05  }))];

      for (const { name, delta } of allPlayers) {
        const part = participants.find(p => p.displayName === name);
        if (!part?.playerId) continue;
        const pRef = fiDoc(db, "clubs", clubId, "players", part.playerId);
        const snap = await fiGet(pRef);
        if (!snap.exists()) continue;
        const data = snap.data() as any;
        const current = data?.ratings?.[sport === "pickleball" ? "pickleball" : sport === "padel" ? "padel" : "tennis"] ?? 3.0;
        await fiUpdate(pRef, { [ratingKey]: CLAMP(current + delta) });
      }

      setTeam1([]); setTeam2([]); setScore1(""); setScore2("");
      setShowForm(false);
    } finally { setSaving(false); }
  }

  return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
          ⚡ {lang === "fi" ? "Haastapelit" : "Challenge Games"}
        </div>
        {!showForm && (
          <button onClick={() => setShowForm(true)} style={{
            ...btn, padding: "6px 12px", fontSize: 12,
            background: C.navy, color: C.white,
          }}>{lang === "fi" ? "+ Lisää peli" : "+ Add game"}</button>
        )}
      </div>

      {/* Lisäyslomake */}
      {showForm && (
        <div style={{ background: C.soft, borderRadius: 12, padding: 14, marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 8 }}>{lang === "fi" ? "Valitse pelaajat" : "Select players"}</div>

          {/* Joukkue 1 */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: C.navy, fontWeight: 700, marginBottom: 6 }}>
              {lang === "fi" ? "Joukkue 1" : "Team 1"} {team1.length > 0 ? `(${team1.join(" & ")})` : ""}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {names.map(n => (
                <button key={n} type="button" onClick={() => togglePlayer(n, 1)} style={{
                  padding: "5px 10px", borderRadius: 8, border: "none", cursor: "pointer",
                  fontSize: 12, fontWeight: 600, fontFamily: F.body,
                  background: team1.includes(n) ? C.navy : team2.includes(n) ? "#e5e7eb" : C.white,
                  color: team1.includes(n) ? C.white : team2.includes(n) ? C.muted : C.text,
                  opacity: team2.includes(n) ? 0.4 : 1,
                }}>{n}</button>
              ))}
            </div>
          </div>

          {/* Joukkue 2 */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: C.gold, fontWeight: 700, marginBottom: 6 }}>
              {lang === "fi" ? "Joukkue 2" : "Team 2"} {team2.length > 0 ? `(${team2.join(" & ")})` : ""}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {names.map(n => (
                <button key={n} type="button" onClick={() => togglePlayer(n, 2)} style={{
                  padding: "5px 10px", borderRadius: 8, border: "none", cursor: "pointer",
                  fontSize: 12, fontWeight: 600, fontFamily: F.body,
                  background: team2.includes(n) ? C.gold : team1.includes(n) ? "#e5e7eb" : C.white,
                  color: team2.includes(n) ? C.white : team1.includes(n) ? C.muted : C.text,
                  opacity: team1.includes(n) ? 0.4 : 1,
                }}>{n}</button>
              ))}
            </div>
          </div>

          {/* Tulos */}
          {team1.length > 0 && team2.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <input value={score1} onChange={e => setScore1(e.target.value)} type="number" min="0"
                placeholder="0" style={{ ...input, width: 60, textAlign: "center", marginBottom: 0, fontSize: 20, fontWeight: 700 }} />
              <span style={{ fontSize: 16, color: C.muted, fontWeight: 700 }}>–</span>
              <input value={score2} onChange={e => setScore2(e.target.value)} type="number" min="0"
                placeholder="0" style={{ ...input, width: 60, textAlign: "center", marginBottom: 0, fontSize: 20, fontWeight: 700 }} />
            </div>
          )}

          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={saveChallenge} disabled={saving || team1.length === 0 || team2.length === 0 || score1 === "" || score2 === ""}
              style={{ ...btn, flex: 1, background: C.navy, color: C.white, opacity: saving ? 0.6 : 1 }}>
              {saving ? (lang === "fi" ? "Tallennetaan..." : "Saving...") : "✔ " + (lang === "fi" ? "Tallenna" : "Save")}
            </button>
            <button onClick={() => { setShowForm(false); setTeam1([]); setTeam2([]); setScore1(""); setScore2(""); }}
              style={{ ...btn, flex: 1, background: C.soft, color: C.text }}>
              {lang === "fi" ? "Peruuta" : "Cancel"}
            </button>
          </div>
        </div>
      )}

      {/* Lista haastapeleistä */}
      {challenges.length === 0 && !showForm && (
        <div style={{ textAlign: "center", padding: "16px 0", color: C.muted, fontSize: 13 }}>
          {lang === "fi" ? "Ei haastapelejä vielä — haasta kaveri! ⚡" : "No challenge games yet — challenge someone! ⚡"}
        </div>
      )}
      {challenges.map((ch: any) => {
        const w1 = ch.score1 > ch.score2;
        return (
          <div key={ch.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: C.soft, borderRadius: 10, marginBottom: 6 }}>
            <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: w1 ? C.navy : C.muted, textAlign: "right" }}>
              {ch.team1.join(" & ")}
            </span>
            <span style={{ fontSize: 15, fontWeight: 900, fontFamily: "monospace", color: C.text, minWidth: 48, textAlign: "center" }}>
              {ch.score1}–{ch.score2}
            </span>
            <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: !w1 ? C.navy : C.muted }}>
              {ch.team2.join(" & ")}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── ProfileTab ────────────────────────────────────────────────
function ProfileTab({ player, session, lang }: { player: Player | null; session: AuthSession; lang: "fi"|"en" }) {
  const [duprState, setDuprState] = useState<DuprConnectState | undefined>(
    player?.duprId ? {
      duprId: player.duprId,
      singlesRating: player.duprSinglesRating ?? undefined,
      doublesRating: player.duprDoublesRating ?? undefined,
      hasBasicL1: player.duprHasBasicL1,
      linkedAt: player.duprLinkedAt,
    } : undefined
  );

  async function handleDuprConnected(state: DuprConnectState | null) {
    setDuprState(state ?? undefined);
    if (!session.playerId) return;
    const { doc: fiDoc, updateDoc, deleteField } = await import("firebase/firestore");
    const { db } = await import("../firebase");
    const ref = fiDoc(db, "clubs", session.clubId, "players", session.playerId);
    if (state === null) {
      // Poista DUPR-tiedot Firestorestä
      await updateDoc(ref, {
        duprId: deleteField(), duprDoublesRating: deleteField(), duprSinglesRating: deleteField(),
        duprLinkedAt: deleteField(), duprHasBasicL1: deleteField(), duprEntitlements: deleteField(),
      });
      return;
    }
    // Palvelin kirjoittaa duprLinkedAt:n itse; tämä varmistaa sen myös
    // silloin, kun vanha funktioversio ei vielä palauta aikaleimaa.
    if (!state.linkedAt) {
      const linkedAt = new Date().toISOString();
      setDuprState({ ...state, linkedAt });
      try { await updateDoc(ref, { duprLinkedAt: linkedAt }); } catch (err) { console.error("duprLinkedAt", err); }
    }
  }

  // Profiilikuva. Alkuarvo pelaajadokumentista; päivittyy paikallisesti
  // heti latauksen jälkeen, jotta kuva vaihtuu ilman uudelleenlatausta.
  const [photoUrl, setPhotoUrl] = useState<string>(player?.photoUrl || "");
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState("");
  useEffect(() => { setPhotoUrl(player?.photoUrl || ""); }, [player?.photoUrl]);

  /**
   * Ottaa vastaan valitun tiedoston, skaalaa sen ja tallentaa.
   * @param file Käyttäjän valitsema kuva.
   */
  async function uploadPhoto(file: File) {
    if (!session.playerId) return;
    setPhotoBusy(true);
    setPhotoError("");
    try {
      const small = await shrinkToSquare(file);
      const { getStorage, ref, uploadBytes, getDownloadURL } = await import("firebase/storage");
      const { app, db: fdb } = await import("../firebase");
      const { doc: fiDoc, updateDoc: fiUpdate } = await import("firebase/firestore");
      const path = `player-photos/${session.clubId}_${session.playerId}_${Date.now()}.jpg`;
      const storage = getStorage(app);
      const sref = ref(storage, path);
      await uploadBytes(sref, small, { contentType: "image/jpeg" });
      const url = await getDownloadURL(sref);
      await fiUpdate(fiDoc(fdb, "clubs", session.clubId, "players", session.playerId), { photoUrl: url });
      // Vanha kuva pois, jottei jokainen vaihto jätä tiedostoa jälkeensä.
      // ref() hyväksyy myös lataus-URL:n, joten polkua ei tarvitse säilöä
      // erikseen. Epäonnistuminen ei ole virhe: uusi kuva on jo tallessa.
      if (photoUrl) {
        const { deleteObject } = await import("firebase/storage");
        await deleteObject(ref(storage, photoUrl)).catch(() => undefined);
      }
      setPhotoUrl(url);
    } catch {
      setPhotoError(lang === "fi"
        ? "Kuvan tallennus ei onnistunut. Yritä uudelleen."
        : "Could not save the picture. Please try again.");
    }
    setPhotoBusy(false);
  }

  /**
   * Poistaa kuvan sekä pelaajadokumentista että Storagesta.
   *
   * Pelkkä kentän tyhjennys ei riittäisi: lukuoikeus tiedostoon on auki,
   * joten kuva olisi yhä haettavissa osoitteellaan. Tietosuojaseloste
   * lupaa poiston, ja tämän on tarkoitettava sitä.
   */
  async function removePhoto() {
    if (!session.playerId) return;
    setPhotoBusy(true);
    try {
      const { db: fdb, app } = await import("../firebase");
      const { doc: fiDoc, updateDoc: fiUpdate, deleteField } = await import("firebase/firestore");
      await fiUpdate(fiDoc(fdb, "clubs", session.clubId, "players", session.playerId), { photoUrl: deleteField() });
      if (photoUrl) {
        const { getStorage, ref, deleteObject } = await import("firebase/storage");
        await deleteObject(ref(getStorage(app), photoUrl)).catch(() => undefined);
      }
      setPhotoUrl("");
    } catch {
      setPhotoError(lang === "fi" ? "Poisto ei onnistunut." : "Could not remove the picture.");
    }
    setPhotoBusy(false);
  }

  const [notifStatus, setNotifStatus] = useState<"idle"|"loading"|"ok"|"denied"|"error">(
    () => webNotificationPermission() === "granted" ? "ok" : "idle"
  );
  // Lupa ja token ovat eri asioita, ja tama kohta sekoitti ne.
  //
  // Kun tilaksi tulee "ok", nappia ei renderoida lainkaan — tilalle
  // tulee "Ilmoitukset paalla" -kortti. registerFcmToken kutsuttiin
  // kuitenkin vain siita napista, joten pelaaja joka oli antanut luvan
  // aiemmin nakit vihrean kortin ilman etta yhtaan tokenia oli
  // koskaan rekisteroity. Palvelin lahetti oikein, laitteelle ei vain
  // ollut osoitetta.
  //
  // Rekisteroidaan siis aina kun lupa on olemassa. Kirjoitus on merge
  // samaan dokumenttiin, joten toisto on halpaa — ja tokenit myos
  // vaihtuvat ajoittain, joten uudelleenrekisterointi on tarpeen.
  useEffect(() => {
    notificationsGranted().then(granted => {
      if (!granted) return;
      setNotifStatus("ok");
      registerFcmToken("player", session.clubId, "player", session.playerName)
        .catch(() => { /* nayton tila ei muutu; yritetaan taas ensi avauksella */ });
    });
  }, [session.clubId, session.playerName]);
  const [testResult, setTestResult] = useState<"idle"|"sent"|"fail">("idle");
  // Mika tarkalleen tapahtui. Yksi yleiskatch ilman erittelya maksoi
  // kaksi turhaa testikierrosta: "ei tullut" ei kerro kaatuiko kutsu,
  // puuttuiko service worker vai nielikö kayttojarjestelma ilmoituksen.
  const [testDetail, setTestDetail] = useState<string>("");

  // iOS-tunnistus: Safari jossa ei ole PWA asennettuna.
  //
  // Natiiviapissa on oltava !isNative(): Capacitorin WebView'n
  // käyttäjäagentti sisältää "iPhone", mutta display-mode ei ole
  // standalone eikä navigator.standalone ole tosi — kumpikaan ei
  // päde WKWebView'ssä. Ilman tätä ehtoa kaupasta asennettu
  // sovellus näytti "asenna kotinäytölle" -ohjeen ilmoitusnapin
  // sijaan, eikä registerFcmToken ehtinyt koskaan ajoon. Natiivi
  // iOS ei siis pystynyt rekisteröimään FCM-tokenia lainkaan.
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isInPWA = window.matchMedia("(display-mode: standalone)").matches
    || (window.navigator as any).standalone === true;
  const isIOSBrowser = isIOS && !isInPWA && !isNative();
  const [stats, setStats] = useState<{ events: number; wins: number; losses: number; pts: number } | null>(null);
  const [duprStats, setDuprStats] = useState<DuprPlayerStats | null>(null);
  const [duprStatsLoading, setDuprStatsLoading] = useState(false);
  const [duprStatsFailed, setDuprStatsFailed] = useState(false);

  // Hae DUPR-tilastot kun pelaajalla on duprId
  useEffect(() => {
    const duprId = duprState?.duprId ?? player?.duprId;
    if (!duprId) return;
    setDuprStatsLoading(true);
    setDuprStatsFailed(false);
    duprGetPlayerStats({ duprId })
      .then(r => setDuprStats(r.data))
      // Virhe näytetään käyttäjälle. Aiemmin se niellettiin, joten
      // DUPR:n katkos näytti siltä että pelaajalla ei ole ratingia.
      .catch(() => setDuprStatsFailed(true))
      .finally(() => setDuprStatsLoading(false));
  }, [duprState?.duprId, player?.duprId]);

  // Tuore rating talteen pelaajadokumenttiin. Adminin parivihje
  // (AdminView: ratingByName) lukee duprDoublesRating-kenttää, joka
  // kirjoitettiin vain DUPR-tiliä yhdistäessä. Jos DUPR antoi ratingin
  // vasta myöhemmin, parivihje ei näkisi sitä koskaan. Kirjoitetaan vain
  // kun arvo on oikeasti muuttunut, ettei jokainen profiilin avaus
  // aiheuta turhaa kirjoitusta.
  useEffect(() => {
    if (!duprStats || !session.playerId) return;
    const d = duprStats.doublesRating ?? null;
    const s = duprStats.singlesRating ?? null;
    if (d === null && s === null) return;
    if (d === (player?.duprDoublesRating ?? null) &&
        s === (player?.duprSinglesRating ?? null)) return;
    const upd: Record<string, number> = {};
    if (d !== null) upd.duprDoublesRating = d;
    if (s !== null) upd.duprSinglesRating = s;
    updateDoc(doc(db, "clubs", session.clubId, "players", session.playerId), upd)
      .catch(() => { /* näyttö toimii silti; yritetään uudestaan ensi avauksella */ });
  }, [duprStats, player?.duprDoublesRating, player?.duprSinglesRating,
      session.clubId, session.playerId]);

  useEffect(() => {
    async function loadStats() {
      if (!session.playerName) return;
      const name = session.playerName.toLowerCase();
      let evSnap;
      try {
        evSnap = await getDocs(query(collection(db, "clubs", session.clubId, "events"), orderBy("createdAt", "desc")));
      } catch {
        evSnap = await getDocs(collection(db, "clubs", session.clubId, "events"));
      }
      let totalWins = 0, totalLosses = 0, totalPts = 0, eventsPlayed = 0;
      await Promise.all(evSnap.docs.map(async eDoc => {
        const evData = eDoc.data();
        if (evData.status !== "finished") return; // vain päättyneet tapahtumat
        const gSnap = await getDocs(collection(db, "clubs", session.clubId, "events", eDoc.id, "games"));
        const games = gSnap.docs.map(d => d.data() as Game);
        let played = 0;
        games.forEach(g => {
          const result = gameResult(g);
          if (result === "unplayed") return;
          const inT1 = g.team1.some(n => n.toLowerCase() === name);
          const inT2 = g.team2.some(n => n.toLowerCase() === name);
          if (!inT1 && !inT2) return;
          played++;
          totalPts += (inT1 ? g.score1 : g.score2) as number;
          if (result === "tie") return;
          if ((inT1 && result === "team1") || (inT2 && result === "team2")) totalWins++;
          else totalLosses++;
        });
        if (played > 0) eventsPlayed++;
      }));
      setStats({ events: eventsPlayed, wins: totalWins, losses: totalLosses, pts: totalPts });
    }
    loadStats();
  }, [session]);

  const ratings = player?.ratings || {};

  return (
    <div>
      {/* Avatar — klikkaus vaihtaa kuvan */}
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <label style={{
          position: "relative", display: "block",
          width: 72, height: 72, margin: "0 auto 12px",
          cursor: photoBusy ? "default" : "pointer",
        }} title={lang === "fi" ? "Vaihda kuva" : "Change picture"}>
          <Avatar
            name={session.playerName} photoUrl={photoUrl} size={72}
            background={`linear-gradient(135deg, ${C.navy}, #2a5c8f)`}
            color={C.white}
            style={{
              fontWeight: 900,
              boxShadow: "0 8px 20px rgba(22,59,104,0.20)",
              opacity: photoBusy ? 0.5 : 1,
            }}
          />
          {/* Kamerakuvake kertoo että ympyrä on painettava — ilman sitä
              koko toiminto jäisi löytymättä. */}
          <div style={{
            position: "absolute", right: -2, bottom: -2,
            width: 26, height: 26, borderRadius: "50%",
            background: C.card, border: `1px solid ${C.border}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 13, boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
          }}>
            {photoBusy ? "⏳" : "📷"}
          </div>
          <input
            type="file" accept="image/*" disabled={photoBusy}
            style={{ display: "none" }}
            onChange={e => {
              const f = e.target.files?.[0];
              // Nollataan arvo, jotta saman tiedoston voi valita uudelleen
              // epäonnistuneen yrityksen jälkeen.
              e.target.value = "";
              if (f) uploadPhoto(f);
            }}
          />
        </label>
        <div style={{ fontSize: 22, fontWeight: 700, color: C.gold, fontFamily: F.heading }}>
          {session.playerName}
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
          {session.clubName} · {lang === "fi" ? "Pelaaja" : "Player"}
        </div>
        {photoError && (
          <div style={{ fontSize: 11, color: C.error, marginTop: 6 }}>{photoError}</div>
        )}
        {photoUrl && !photoBusy && (
          <button onClick={removePhoto} style={{
            marginTop: 6, background: "none", border: "none", cursor: "pointer",
            fontSize: 11, color: C.muted, fontFamily: F.body, textDecoration: "underline",
          }}>
            {lang === "fi" ? "Poista kuva" : "Remove picture"}
          </button>
        )}
      </div>

      {/* Tilastot */}
      {stats && (stats.events > 0) && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {[
            { label: lang === "fi" ? "Turnaukset" : "Tournaments", value: stats.events },
            { label: lang === "fi" ? "Voitot"     : "Wins",        value: stats.wins },
            { label: lang === "fi" ? "Voitto-%"   : "Win %",       value: (stats.wins + stats.losses) > 0 ? `${Math.round(stats.wins / (stats.wins + stats.losses) * 100)}%` : "—" },
          ].map(s => (
            <div key={s.label} style={{ flex: 1, ...card, marginBottom: 0, textAlign: "center", padding: 12 }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: C.navy, fontFamily: F.heading }}>{s.value}</div>
              <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Ratingit — lasketaan suoraan stats-datasta jos ei Firestoressä */}
      {(() => {
        const totalGames = stats ? stats.wins + stats.losses : 0;
        // Tuore haku ensin, sitten juuri yhdistetty tili, sitten Firestore.
        // Aiemmin tässä luettiin vain kahta jälkimmäistä, eli sitä arvoa joka
        // tallennettiin DUPR-tiliä yhdistäessä. Jos DUPR antoi ratingin vasta
        // myöhemmin, tämä kortti väitti loputtomiin ettei ratingia ole —
        // vaikka alempi DUPR-kortti näytti sen samalla ruudulla.
        const doublesRating = duprStats?.doublesRating ?? duprState?.doublesRating ?? player?.duprDoublesRating ?? null;
        const singlesRating = duprStats?.singlesRating ?? duprState?.singlesRating ?? player?.duprSinglesRating ?? null;
        const hasDuprRating = !!doublesRating || !!singlesRating;

        // Yhdistetty tili ilman ratingia on eri asia kuin yhdistämätön:
        // kehotus yhdistää on väärä neuvo pelaajalle, joka on jo tehnyt sen.
        const duprLinked = !!(duprState?.duprId || player?.duprId);
        if (!hasDuprRating && !ratings.padel && !ratings.tennis) return (
          <div style={{ ...card, textAlign: "center", padding: 32 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🏆</div>
            {/* Kesken oleva ja epäonnistunut haku eivät ole sama asia kuin
                "ratingia ei ole". Kortti väitti molemmissa tapauksissa ettei
                ratingia ole, mikä on väärä tieto pelaajalle — katkoksen
                aikana erityisesti. */}
            <div style={{ fontSize: 13, color: C.muted }}>
              {!duprLinked
                ? (lang === "fi"
                  ? "Yhdistä DUPR-tili profiilissasi nähdäksesi ratingin."
                  : "Connect your DUPR account in your profile to see your rating.")
                : duprStatsLoading
                ? (lang === "fi"
                  ? "Haetaan DUPR-ratingia..."
                  : "Fetching your DUPR rating...")
                : duprStatsFailed
                ? (lang === "fi"
                  ? "DUPR ei vastaa juuri nyt. Rating näkyy kun yhteys palaa."
                  : "DUPR is not responding right now. Your rating will appear once the connection is back.")
                : duprStats?.consentRequired
                // DUPR palautti 403. Tunnus voi olla kirjoitettu käsin
                // ylläpidossa, jolloin ottelut lähtevät perille mutta
                // ratingia ei saa lukea ennen kuin pelaaja on itse
                // yhdistänyt tilinsä. "Ratingia ei ole" olisi väärä tieto
                // eikä kertoisi mitä tehdä.
                ? (lang === "fi"
                  ? "DUPR ei anna lukea ratingiasi ennen kuin olet itse yhdistänyt tilisi. Tee se profiilissasi kohdasta \"Yhdistä DUPR-tili\" — pelkkä DUPR-tunnus ei riitä."
                  : "DUPR will not release your rating until you connect your account yourself. Do it in your profile under \"Connect DUPR Account\" — a DUPR ID alone is not enough.")
                : (lang === "fi"
                  ? "DUPR-tili on yhdistetty, mutta ratingia ei ole vielä saatavilla."
                  : "Your DUPR account is connected, but no rating is available yet.")}
            </div>
          </div>
        );

        return (
          <div style={card}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>
              🏅 DUPR Rating
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              {doublesRating && <RatingCard sport="pickleball" value={doublesRating} nameOverride="Doubles" />}
              {singlesRating && <RatingCard sport="pickleball" value={singlesRating} nameOverride="Singles" />}
              {ratings.padel  && <RatingCard sport="padel"  value={ratings.padel} />}
              {ratings.tennis && <RatingCard sport="tennis" value={ratings.tennis} />}
            </div>
          </div>
        );
      })()}

      {/* Ilmoitukset */}
      <div style={card}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>
          🔔 {lang === "fi" ? "Push-ilmoitukset" : "Push Notifications"}
        </div>

        {/* iOS Safari ilman PWA — näytä asennusohjeet */}
        {isIOSBrowser ? (
          <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#c2410c", marginBottom: 6 }}>
              📱 {lang === "fi" ? "iPhone: asenna sovellus ensin" : "iPhone: install the app first"}
            </div>
            <div style={{ fontSize: 12, color: "#7c3011", lineHeight: 1.7 }}>
              {lang === "fi" ? "iPhonessa push-ilmoitukset vaativat että sovellus on asennettu kotinäytölle:" : "On iPhone, push notifications require the app to be installed to the home screen:"}
            </div>
            <div style={{ marginTop: 10, fontSize: 12, color: "#9a3412", lineHeight: 1.8 }}>
              {lang === "fi" ? (
                <>
                  <div>1. Paina Safarin <strong>Jaa</strong>-painiketta <strong>⬆️</strong></div>
                  <div>2. Valitse <strong>"Lisää kotinäytölle"</strong></div>
                  <div>3. Avaa Spinloop kotinäytöltä</div>
                  <div>4. Tule Profiili-tabille ja salli ilmoitukset</div>
                </>
              ) : (
                <>
                  <div>1. Tap Safari's <strong>Share</strong> button <strong>⬆️</strong></div>
                  <div>2. Select <strong>"Add to Home Screen"</strong></div>
                  <div>3. Open Spinloop from home screen</div>
                  <div>4. Go to Profile tab and allow notifications</div>
                </>
              )}
            </div>
          </div>
        ) : notifStatus === "ok" ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "#f0fdf4", borderRadius: 10, border: "1px solid #bbf7d0" }}>
            <span style={{ fontSize: 20 }}>✅</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#166534" }}>{lang === "fi" ? "Ilmoitukset päällä" : "Notifications enabled"}</div>
              <div style={{ fontSize: 11, color: C.muted }}>{lang === "fi" ? "Saat ilmoituksen uusista tuloksista" : "You'll be notified of new results"}</div>
            </div>
          </div>
        ) : notifStatus === "denied" ? (
          <div style={{ padding: "12px 14px", background: "#fef2f2", borderRadius: 10, border: "1px solid #fecaca" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.error }}>{lang === "fi" ? "Ilmoitukset estetty" : "Notifications blocked"}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4, lineHeight: 1.6 }}>
              {/* Natiiviapissa lupa nollataan laitteen asetuksista, ei selaimen kohdalta */}
              {isNative()
                ? (lang === "fi" ? <>Salli ilmoitukset: <strong>Asetukset → Spinloop → Ilmoitukset → Salli</strong></> : <>Allow notifications: <strong>Settings → Spinloop → Notifications → Allow</strong></>)
                : (lang === "fi" ? <>Salli ilmoitukset: <strong>Asetukset → Safari/Chrome → spinloop.app → Ilmoitukset → Salli</strong></> : <>Allow notifications: <strong>Settings → Safari/Chrome → spinloop.app → Notifications → Allow</strong></>)}
            </div>
          </div>
        ) : (
          <button
            onClick={async () => {
              setNotifStatus("loading");
              const token = await registerFcmToken("player", session.clubId, "player", session.playerName);
              if (token) { setNotifStatus("ok"); return; }
              // Ilman tokenia: lupa kiellettu vai muu virhe?
              setNotifStatus(await notificationsGranted() ? "error" : "denied");
            }}
            disabled={notifStatus === "loading"}
            style={{ ...btn, width: "100%", background: C.navy, color: C.white, opacity: notifStatus === "loading" ? 0.6 : 1 }}
          >
            {notifStatus === "loading" ? (lang === "fi" ? "Rekisteröidään..." : "Registering...") : notifStatus === "error" ? (lang === "fi" ? "Yritä uudelleen" : "Try again") : "🔔 " + (lang === "fi" ? "Salli ilmoitukset" : "Allow notifications")}
          </button>
        )}

        {/* Testipainike */}
        {notifStatus === "ok" && !isIOSBrowser && !isNative() && (
          <div style={{ marginTop: 10 }}>
            <button
              onClick={async () => {
                const title = lang === "fi" ? "Spinloop — testi" : "Spinloop — test";
                const options = {
                  body: lang === "fi" ? "Ilmoitukset toimivat! Saat tiedot tuloksista tähän." : "Notifications are working! You'll receive score updates here.",
                  icon: "/logo192.png",
                };
                try {
                  // Notification-konstruktori toimii vain työpöytäselaimissa.
                  // Android Chrome heittää "Illegal constructor" ja iOS:n
                  // kotinäyttösovellus ei tunne sitä lainkaan — molemmissa
                  // ilmoitus on näytettävä service workerin kautta.
                  //
                  // getRegistration() ilman argumenttia etsii workerin,
                  // jonka scope kattaa nykyisen sivun. Sellaista ei ole:
                  // index.tsx kutsuu unregister(), joten CRA:n omaa
                  // workeria ei rekisteröidä, ja Firebase rekisteröi omansa
                  // scopeen /firebase-cloud-messaging-push-scope. Siksi
                  // käydään läpi kaikki rekisteröinnit — showNotification
                  // toimii mistä tahansa niistä riippumatta scopesta.
                  const sw = navigator.serviceWorker;
                  const reg = sw
                    ? (await sw.getRegistration()) ?? (await sw.getRegistrations())[0]
                    : undefined;
                  setTestDetail("");
                  if (reg) {
                    await reg.showNotification(title, options);
                  } else if (typeof Notification === "function") {
                    new Notification(title, options);
                    setTestDetail(lang === "fi"
                      ? "Service workeria ei löytynyt — käytettiin selaimen omaa ilmoitusta."
                      : "No service worker found — used the browser's own notification.");
                  } else {
                    throw new Error("Notification-rajapintaa ei ole");
                  }
                  setTestResult("sent");
                } catch (err) {
                  setTestResult("fail");
                  // Virheen nimi kertoo syyn: NotAllowedError = lupa,
                  // TypeError = konstruktori ei tuettu, InvalidStateError
                  // = service worker ei aktiivinen.
                  setTestDetail(err instanceof Error ? `${err.name}: ${err.message}` : String(err));
                }
                setTimeout(() => setTestResult("idle"), 12000);
              }}
              style={{ ...btn, width: "100%", background: C.soft, color: C.navy, border: `1px solid ${C.border}`, fontSize: 12 }}
            >
              📳 {lang === "fi" ? "Lähetä testiviesti" : "Send test notification"}
            </button>
            {testResult === "sent" && (
              <div style={{ fontSize: 11, color: "#166534", textAlign: "center", marginTop: 6, lineHeight: 1.5 }}>
                ✅ {lang === "fi" ? "Selain otti ilmoituksen vastaan" : "The browser accepted the notification"}
                {testDetail && <div style={{ color: C.muted, marginTop: 2 }}>{testDetail}</div>}
                <div style={{ color: C.muted, marginTop: 2 }}>
                  {lang === "fi"
                    ? "Jos ilmoitusta ei näy, se on estetty käyttöjärjestelmän asetuksissa."
                    : "If no notification appeared, it is blocked in your operating system settings."}
                </div>
              </div>
            )}
            {testResult === "fail" && (
              <div style={{ fontSize: 11, color: C.error, textAlign: "center", marginTop: 6, lineHeight: 1.5 }}>
                {lang === "fi" ? "Ilmoitus ei onnistunut" : "The notification failed"}
                {testDetail && <div style={{ marginTop: 2, fontFamily: "monospace" }}>{testDetail}</div>}
              </div>
            )}
          </div>
        )}
      </div>

      {/* DUPR SSO + tilastot */}
      <div style={card}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>
          🏅 {lang === "fi" ? "Virallinen DUPR-tili" : "Official DUPR Account"}
        </div>
        {session.playerId && (
          <DuprConnectButton
            playerId={session.playerId}
            clubId={session.clubId}
            playerName={session.playerName || ""}
            current={duprState}
            onConnected={handleDuprConnected}
            lang={lang}
          />
        )}

        {/* DUPR-tilastot yhdistämisen jälkeen */}
        {(duprState?.duprId || player?.duprId) && (
          <div style={{ marginTop: 14 }}>
            {duprStatsLoading ? (
              <div style={{ fontSize: 12, color: C.muted, textAlign: "center", padding: "8px 0" }}>
                {lang === "fi" ? "Haetaan DUPR-tilastoja..." : "Fetching DUPR stats..."}
              </div>
            ) : duprStatsFailed ? (
              <div style={{ fontSize: 12, color: C.muted, textAlign: "center", padding: "8px 0" }}>
                {lang === "fi"
                  ? "DUPR ei vastaa juuri nyt. Tilastot päivittyvät kun yhteys palaa."
                  : "DUPR is not responding right now. Stats will update once the connection is back."}
              </div>
            ) : duprStats ? (
              <>
                {/* Rating-palkit */}
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  {duprStats.doublesRating != null && (
                    <div style={{
                      flex: 1, background: "linear-gradient(135deg,var(--sl-primary),#1a56db)",
                      borderRadius: 10, padding: "10px 12px", textAlign: "center",
                    }}>
                      <div style={{ fontSize: 10, color: "rgba(255,255,255,0.7)", marginBottom: 2 }}>
                        {lang === "fi" ? "Parirating" : "Doubles"}
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", fontFamily: F.heading }}>
                        {duprStats.doublesRating.toFixed(3)}
                      </div>
                    </div>
                  )}
                  {duprStats.singlesRating != null && (
                    <div style={{
                      flex: 1, background: "linear-gradient(135deg,#0f4c2a,#16a34a)",
                      borderRadius: 10, padding: "10px 12px", textAlign: "center",
                    }}>
                      <div style={{ fontSize: 10, color: "rgba(255,255,255,0.7)", marginBottom: 2 }}>
                        {lang === "fi" ? "Singlesrating" : "Singles"}
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", fontFamily: F.heading }}>
                        {duprStats.singlesRating.toFixed(3)}
                      </div>
                    </div>
                  )}
                </div>

                {/* Voitot / Häviöt */}
                {(duprStats.wins > 0 || duprStats.losses > 0) && (
                  <div style={{ display: "flex", gap: 8 }}>
                    <div style={{
                      flex: 1, background: "#f0fdf4", border: "1px solid #bbf7d0",
                      borderRadius: 8, padding: "8px 10px", textAlign: "center",
                    }}>
                      <div style={{ fontSize: 10, color: "#16a34a", fontWeight: 600, marginBottom: 1 }}>
                        {lang === "fi" ? "Voitot" : "Wins"}
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: "#15803d" }}>
                        {duprStats.wins}
                      </div>
                    </div>
                    <div style={{
                      flex: 1, background: "#fef2f2", border: "1px solid #fecaca",
                      borderRadius: 8, padding: "8px 10px", textAlign: "center",
                    }}>
                      <div style={{ fontSize: 10, color: "#dc2626", fontWeight: 600, marginBottom: 1 }}>
                        {lang === "fi" ? "Häviöt" : "Losses"}
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: "#b91c1c" }}>
                        {duprStats.losses}
                      </div>
                    </div>
                    <div style={{
                      flex: 1, background: C.soft, border: `1px solid ${C.border}`,
                      borderRadius: 8, padding: "8px 10px", textAlign: "center",
                    }}>
                      <div style={{ fontSize: 10, color: C.muted, fontWeight: 600, marginBottom: 1 }}>
                        {lang === "fi" ? "Voitto-%" : "Win %"}
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: C.text }}>
                        {duprStats.wins + duprStats.losses > 0
                          ? `${Math.round(duprStats.wins / (duprStats.wins + duprStats.losses) * 100)}%`
                          : "—"}
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ fontSize: 10, color: C.muted, textAlign: "right", marginTop: 6 }}>
                  {lang === "fi" ? "Lähde: DUPR" : "Source: DUPR"}
                </div>
              </>
            ) : null}
          </div>
        )}
      </div>

      {/* Versio & info */}
      <div style={{ ...card, textAlign: "center", padding: "18px 16px" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>
          Spinloop <span style={{ color: C.gold }}>v{APP_VERSION}</span>
        </div>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>
          DeepEnd Solutions Oy
        </div>
        <a href="/privacy" style={{
          fontSize: 11, color: C.navy, textDecoration: "none",
          background: C.soft, borderRadius: 8, padding: "5px 14px",
          display: "inline-block", fontWeight: 600,
        }}>
          {lang === "fi" ? "🔒 Tietosuojakäytäntö" : "🔒 Privacy Policy"}
        </a>
      </div>
    </div>
  );
}

// ── HistoryTab ────────────────────────────────────────────────
type HistoryEntry = { event: ClubEvent; position: number; wins: number; games: number; myGames: Game[] };

// ── LIIGA ────────────────────────────────────────────────────
//
// Taulukko lasketaan kauden päättyneiden iltojen sijoituksista joka
// kerta (utils/league.ts); mitään ei tallenneta. Sama taulukko näkyy
// ylläpitäjälle kausiasetuksissa.
function LeagueTab({ clubId, season, myPlayerId, myName, lang }: {
  clubId: string; season: Season; myPlayerId?: string; myName?: string; lang: "fi"|"en";
}) {
  const { standings, loading, photos } = useLeagueStandings(clubId, season);
  return (
    <div>
      <LeagueTable season={season} standings={standings} loading={loading} myPlayerId={myPlayerId} myName={myName} photos={photos} lang={lang} />
      <LeagueRulesCard season={season} standings={standings} loading={loading} myPlayerId={myPlayerId} myName={myName} lang={lang} />
    </div>
  );
}

/** Liigaillan tapahtumasivulle: säännöt avattavana ja oma sijoitus kaudessa. */
function LeagueEventCard({ clubId, season, myPlayerId, myName, lang }: {
  clubId: string; season: Season; myPlayerId?: string; myName?: string; lang: "fi"|"en";
}) {
  const { standings, loading } = useLeagueStandings(clubId, season);
  return <LeagueRulesCard season={season} standings={standings} loading={loading} myPlayerId={myPlayerId} myName={myName} lang={lang} />;
}

function HistoryTab({ clubId, playerId, playerName, lang }: { clubId: string; playerId?: string; playerName?: string; lang: "fi"|"en" }) {
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      if (!playerName) { setLoading(false); return; }
      // Jarjestys tehdaan selaimessa kentalla datetime, koska se on se
      // paivamaara jonka kortti nayttaa. Aiemmin tassa oli Firestoren
      // orderBy("createdAt", "desc"), eli lista oli luontijarjestyksessa:
      // myohemmin luotu aiempi ilta nousi karkeen, ja aikajana nappasi
      // taaksepain kesken listan. createdAt ei myoskaan ole samassa
      // muodossa kaikissa dokumenteissa, mika oli syy alla olevaan
      // fallbackiin — kun jarjestys on omissa kasissa, sita ei tarvita.
      const evSnap = await getDocs(collection(db, "clubs", clubId, "events"));
      const evDocs = [...evSnap.docs].sort((a, b) => {
        const da = (a.data() || {}).datetime || "";
        const dbb = (b.data() || {}).datetime || "";
        return String(dbb).localeCompare(String(da));
      });

      const results = await Promise.all(evDocs.map(async eDoc => {
        const ev = { id: eDoc.id, ...eDoc.data() } as ClubEvent;
        const gSnap = await getDocs(collection(db, "clubs", clubId, "events", ev.id, "games"));
        const allGames = gSnap.docs.map(d => ({ id: d.id, ...d.data() } as Game));

        let wins = 0, played = 0;
        const myGames: Game[] = [];

        allGames.forEach(g => {
          const result = gameResult(g);
          if (result === "unplayed") return;
          const inT1 = g.team1.some(n => n.toLowerCase() === playerName.toLowerCase());
          const inT2 = g.team2.some(n => n.toLowerCase() === playerName.toLowerCase());
          if (!inT1 && !inT2) return;
          played++;
          myGames.push(g);
          if ((inT1 && result === "team1") || (inT2 && result === "team2")) wins++;
        });

        if (played === 0) return null;

        // Sijoitus lasketaan tapahtuman kaikista otteluista, ei vain
        // omista: isommassa tapahtumassa pelaaja kohtaa vain osan
        // kentästä, joten omista peleistä laskettu taulukko jättäisi
        // kilpailijoita kokonaan pois. Sama funktio kuin adminin
        // pistetaulukossa, joten sijat eivät voi erota toisistaan.
        const ranking = rankPlayers(allGames);
        // Jaettu sija: tasapisteissä sama numero kuin adminin taulukossa
        // ja sama kuin se josta liigapisteet lasketaan.
        const idx = ranking.findIndex(r => r.name.toLowerCase() === playerName.toLowerCase());
        const pos = idx < 0 ? 0 : sharedPositions(ranking)[idx];
        return { event: ev, position: pos || 0, wins, games: played, myGames };
      }));

      setHistory(results.filter(Boolean) as HistoryEntry[]);
      setLoading(false);
    }
    load();
  }, [clubId, playerName]);

  if (loading) return <div style={{ textAlign: "center", padding: 40, color: C.muted }}>{lang === "fi" ? "Ladataan..." : "Loading..."}</div>;

  const totalWins  = history.reduce((s, h) => s + h.wins, 0);
  const totalGames = history.reduce((s, h) => s + h.games, 0);

  return (
    <div>
      {history.length > 0 && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {[
            { label: lang === "fi" ? "Turnaukset" : "Tournaments", value: history.length },
            { label: lang === "fi" ? "Voitot"     : "Wins",        value: totalWins },
            { label: lang === "fi" ? "Voitto-%"   : "Win %",       value: totalGames > 0 ? `${Math.round(totalWins/totalGames*100)}%` : "—" },
          ].map(s => (
            <div key={s.label} style={{ flex: 1, ...card, marginBottom: 0, textAlign: "center", padding: 12 }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: C.navy, fontFamily: F.heading }}>{s.value}</div>
              <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {history.map(h => (
        <div key={h.event.id} style={card}>
          <div onClick={() => setExpanded(expanded === h.event.id ? null : h.event.id)}
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                {h.event.venue || (lang === "fi" ? "Tapahtuma" : "Event")}
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                {new Date(h.event.datetime).toLocaleDateString(lang === "fi" ? "fi-FI" : "en-GB", { weekday:"short", day:"numeric", month:"short" })}
                {" · "}{h.wins}/{h.games} {lang === "fi" ? "voittoa" : "wins"}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 24 }}>
                  {h.position === 1 ? "🥇" : h.position === 2 ? "🥈" : h.position === 3 ? "🥉" : `${h.position}.`}
                </div>
                <div style={{ fontSize: 10, color: C.muted }}>{lang === "fi" ? "sija" : "place"}</div>
              </div>
              <span style={{ fontSize: 14, color: C.muted }}>{expanded === h.event.id ? "▲" : "▼"}</span>
            </div>
          </div>

          {/* Pelatut ottelut */}
          {expanded === h.event.id && (
            <div style={{ marginTop: 12, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
              {h.myGames.sort((a, b) => (a.round ?? 0) - (b.round ?? 0)).map((g, i) => {
                const inT1 = g.team1.some(n => n.toLowerCase() === (playerName || "").toLowerCase());
                const myScore = inT1 ? g.score1 : g.score2;
                const oppScore = inT1 ? g.score2 : g.score1;
                const myTeam = inT1 ? g.team1 : g.team2;
                const oppTeam = inT1 ? g.team2 : g.team1;
                const won = (myScore ?? 0) > (oppScore ?? 0);
                return (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "8px 10px", borderRadius: 8, marginBottom: 6,
                    background: won ? "#f0fdf4" : "#fef2f2",
                    border: `1px solid ${won ? "#bbf7d0" : "#fecaca"}`,
                  }}>
                    <span style={{ fontSize: 14 }}>{won ? "✅" : "❌"}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>
                        {myTeam.join(" & ")}
                      </div>
                      <div style={{ fontSize: 11, color: C.muted }}>vs {oppTeam.join(" & ")}</div>
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 900, fontFamily: "monospace", color: won ? "#16a34a" : "#dc2626" }}>
                      {myScore}–{oppScore}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}

      {history.length === 0 && (
        <div style={{ textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🎯</div>
          <div style={{ fontSize: 13, color: C.muted }}>{lang === "fi" ? "Ei vielä pelattuja turnauksia." : "No tournaments played yet."}</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{lang === "fi" ? "Tilastosi näkyvät tässä kun pelaat!" : "Your stats will appear here when you play!"}</div>
        </div>
      )}
    </div>
  );
}

// ── RatingCard ────────────────────────────────────────────────
function RatingCard({ sport, value, nameOverride }: { sport: Sport; value: number; nameOverride?: string }) {
  const maxMap:  Record<Sport, number> = { pickleball: 8, padel: 10, tennis: 16, squash: 10 };
  const nameMap: Record<Sport, string> = { pickleball: "DUPR", padel: "PPR", tennis: "UTR", squash: "Rating" };
  const { label, color } =
    sport === "pickleball" ? duprLevel(value) :
    sport === "padel"      ? pprLevel(value)  :
    sport === "tennis"     ? utrLevel(value)  :
    { label: "Rating", color: C.muted };
  const max  = maxMap[sport];
  const name = nameOverride ?? nameMap[sport];
  const pct  = Math.min(100, ((value - 1) / (max - 1)) * 100);

  return (
    <div style={{
      flex: 1, background: C.card, borderRadius: 20, textAlign: "center",
      boxShadow: `0 8px 32px rgba(22,59,104,0.13), 0 0 0 1px ${C.border}`,
      overflow: "hidden", position: "relative",
    }}>
      {/* Yläreuna-korostus tason värillä */}
      <div style={{ height: 5, background: `linear-gradient(90deg, ${color}, ${C.gold})` }} />

      <div style={{ padding: "16px 14px 18px" }}>
        {/* Nimi-badge */}
        <div style={{
          display: "inline-block", fontSize: 10, fontWeight: 700,
          color: C.muted, letterSpacing: "0.14em", textTransform: "uppercase",
          marginBottom: 10,
        }}>{name}</div>

        {/* Iso kursiivi numero */}
        <div style={{
          fontSize: 56, fontWeight: 700, fontStyle: "italic",
          fontFamily: F.heading, letterSpacing: 1, lineHeight: 1,
          color: C.navy, marginBottom: 2,
        }}>
          {value.toFixed(2)}
        </div>

        {/* Taso-badge */}
        <div style={{
          display: "inline-block",
          background: `${color}18`,
          border: `1px solid ${color}40`,
          borderRadius: 20, padding: "4px 14px",
          fontSize: 12, fontWeight: 700,
          fontFamily: F.heading, letterSpacing: 1.5,
          color: color, marginBottom: 14,
        }}>
          {label.toUpperCase()}
        </div>

        {/* Progressipalkki */}
        <div style={{ background: C.soft, borderRadius: 6, height: 6 }}>
          <div style={{
            background: `linear-gradient(90deg, ${color}, ${C.gold})`,
            borderRadius: 6, height: 6, width: `${pct}%`,
            boxShadow: `0 0 8px ${color}60`,
            transition: "width 1s ease",
          }} />
        </div>
        <div style={{ fontSize: 9, color: C.muted, marginTop: 5 }}>
          {value.toFixed(2)} / {max}
        </div>
      </div>
    </div>
  );
}
